import {
  Box,
  Stack,
  useTheme,
  Container,
  useMediaQuery,
  Collapse,
  Fab,
  Fade,
} from '@mui/material'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import { useTranslation } from 'react-i18next'

import { useCurrentStore, useCartButton, useSectionsScrolling } from 'hooks'
import { MenuCategory } from 'models'
import { useEffect, useRef, useState } from 'react'
import { first, isEmpty, uniq } from 'lodash'
import { KBreadcrumbs } from 'components/core/KBreadcrumbs'
import { StoreName } from 'components/ordering/StoreName'
import { KToggleButton } from 'components/core/KButton'
import { KTab, KTabs } from 'components/core/KTabs'
import { MenuCategorySection } from 'components/ordering/MenuCategorySection'
import { CheckoutFooter } from './CheckoutFooter'

interface OrderingMenuState {
  viewType: 'grid' | 'list'
  categoryTypes: string[]
  categoryType?: string
  categories: MenuCategory[]
  category?: MenuCategory
}

interface OrderingMenuProps {
  breadcrumbs: { label: string; to?: string }[]
}

export const OrderingMenu = ({ breadcrumbs }: OrderingMenuProps) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const { showCartButton } = useCartButton()
  const { store } = useCurrentStore()

  useEffect(() => showCartButton(), [showCartButton])

  const [state, setState] = useState<OrderingMenuState>({
    viewType: 'grid',
    categoryTypes: [],
    categories: [],
  })

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  useEffect(() => {
    setState((state) => ({
      ...state,
      viewType: isMobile || store.default_view === false ? 'list' : 'grid',
    }))
  }, [isMobile, store])

  useEffect(() => {
    if (!store) return

    const originalCategories = store.categories ?? []
    const allCategoryTypes = store.category_types ?? null

    let categoryTypes: string[] = uniq(
      originalCategories.flatMap((c: MenuCategory) => c.category_types ?? []),
    )
    if (allCategoryTypes !== null && allCategoryTypes !== undefined) {
      categoryTypes = allCategoryTypes.filter((type: string) =>
        categoryTypes.includes(type),
      )
    }

    const initialCategoryType = first(categoryTypes)
    const initialCategories = initialCategoryType
      ? originalCategories.filter((cat: MenuCategory) =>
          cat.category_types?.includes(initialCategoryType),
        )
      : originalCategories
    const initialCategory = first(initialCategories)

    setState((s) => ({
      ...(s as any),
      categoryTypes,
      categoryType: initialCategoryType,
      categories: initialCategories,
      category: initialCategory,
    }))
  }, [store])

  const handleCategoryTypeChange = (categoryType: string) => {
    if (isEmpty(categoryType)) {
      return
    }
    const categories = store?.categories?.filter((cat: MenuCategory) =>
      cat.category_types?.includes(categoryType),
    )
    const category = first(categories)
    setState({
      ...(state as any),
      categoryType,
      categories,
      category,
    })
  }

  const collapseRef = useRef<HTMLDivElement | null>(null)
  const {
    scrollToSection,
    scrollToTopAndShowHeader,
    sectionsRef,
    scrollRef,
    shouldShowHeader,
    currentSectionIndex,
  } = useSectionsScrolling()
  const handleCategoryChange = (category: MenuCategory) => {
    const index = state.categories?.findIndex((cat) => cat.id === category.id)
    if (index !== undefined && index !== null) {
      scrollToSection(index)
      setState({ ...state, category: category })
    }
  }
  useEffect(() => {
    const category = state.categories?.[currentSectionIndex]
    if (state.category?.id !== category?.id) {
      setState({ ...state, category: category })
    }
  }, [currentSectionIndex]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!store) {
    return null
  }

  return (
    <Stack sx={{ height: '100%', overflow: 'hidden' }}>
      <Stack sx={{ flexGrow: 1, overflowY: 'auto' }}>
        <Container maxWidth="lg">
          <Collapse in={shouldShowHeader} ref={collapseRef}>
            <Stack sx={{ paddingY: 2, gap: 1.5 }}>
              <KBreadcrumbs breadcrumbs={breadcrumbs} />
              <StoreName store={store} />
              <Stack direction="row" gap={2} sx={{ flexWrap: 'wrap' }}>
                {state.categoryTypes?.map((type, index) => (
                  <KToggleButton
                    key={index}
                    onClick={() => handleCategoryTypeChange(type)}
                    variant={
                      state.categoryType === type ? 'primary' : 'secondary'
                    }
                    size="small"
                    sx={{ minWidth: 132 }}
                  >
                    {type}
                  </KToggleButton>
                ))}
              </Stack>
            </Stack>
          </Collapse>
          <Stack direction="row" spacing={2}>
            <KTabs
              variant="scrollable"
              scrollButtons="auto"
              onChange={(_, newValue) => handleCategoryChange(newValue)}
              value={state.category}
              sx={{ flexGrow: 1 }}
            >
              {state.categories?.map((category, index) => (
                <KTab key={index} label={category.name} value={category} />
              ))}
            </KTabs>
          </Stack>
        </Container>
        <Box
          ref={scrollRef}
          sx={{
            overflowY: 'auto',
            flexGrow: 1,
            backgroundColor: theme.palette.primary.light,
            pb: shouldShowHeader ? 0 : 8,
            transition: 'padding-bottom 0.3s ease',
          }}
        >
          <Container maxWidth="lg">
            <Stack>
              {state.categories?.map((category, index) => (
                <MenuCategorySection
                  key={index}
                  index={index}
                  category={category}
                  viewType={state.viewType}
                  sectionsRef={sectionsRef}
                />
              ))}
            </Stack>
          </Container>
        </Box>
      </Stack>
      <Fade in={!shouldShowHeader}>
        <Fab
          color="secondary"
          aria-label={t('Scroll to top & show header')}
          sx={{ position: 'absolute', bottom: 80, right: 16 }}
          onClick={scrollToTopAndShowHeader}
        >
          <ArrowUpwardIcon />
        </Fab>
      </Fade>

      <CheckoutFooter />
    </Stack>
  )
}
